import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import qs from 'qs';
import { checkAndStoreFcmToken } from 'utils/account/push-notifications';
import { setUserLoginLimitStartTimeLS } from 'utils/account/user-info';
import LocalStorage from 'utils/bom-dom-manipulation/local-storage';
import { setCookieLoginCredentials } from 'utils/bom-dom-manipulation/login-credentials';
import { sendPostMessage } from 'utils/bom-dom-manipulation/send-post-message-to-iframe';
import SessionStorage from 'utils/bom-dom-manipulation/session-storage';
import { VAIX_IFRAME_ID } from 'utils/constants/app/iframe-ids';
import { CookieExpiration } from 'utils/constants/dateInfo/dateInfo';
import {
  MILLISECONDS_IN_SECOND,
  SECONDS_PER_MINUTE
} from 'utils/constants/dateInfo/time-numbers';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { storageKeyName } from 'utils/generic/storage-key-name';
import { loginState } from 'utils/sportsbook/api-post-message-sender';
import {
  AuthData,
  LoginResponse,
  ProfileData
} from 'interfaces/authentication';
import { AfterSignIn } from 'services/afterSignIn';
import {
  getUser,
  sendRestoreLoginPostMsg,
  setTermsAndConditionVersion
} from 'services/authentication';
import { getUserAllNotifications } from 'services/sportsbook/notifications';
import { getSavedLocation } from 'components/QueryRoute';
import { useUpdateUser } from 'hooks/auth/useUpdateUser';
import { setLoginLimit, setUserData, setUserLoggedIn } from 'store/actions';
import { setShowAdditionalInfo } from 'store/actions/app-data';
import { getRegFieldData } from 'store/selectors/app-data';

type TLoginCallbackProps = {
  rememberMe: boolean;
  userGroups?: string | null;
  username: string | null;
  password: string | null;
  inPopup?: boolean;
};

type ReturnType = {
  loginCallback: (data: LoginResponse) => void;
  updateAuthData: () => void;
  isTwoFactorPopupVisible: boolean;
  qrCodeOrigin?: string;
};

const TWO_FACTOR_AUTHENTICATION_STATUS = 4;

export const useLoginCallback = ({
  rememberMe,
  userGroups,
  inPopup
}: TLoginCallbackProps): ReturnType => {
  const authData = useRef<AuthData>();
  const [isTwoFactorPopupVisible, setIsTwoFactorPopupVisible] = useState(false);
  const [qrCodeOrigin, setQrCodeOrigin] = useState<string>();
  const dispatch = useDispatch();
  const history = useHistory();

  const regFieldsData = useSelector(getRegFieldData);

  const personalId = regFieldsData?.find(
    field => field && field.formType === 'personalId'
  );

  const loginCallback = (data: LoginResponse) => {
    const {
      auth_token,
      user_id,
      qr_code_origin,
      authentication_status,
      jwe_token
    } = data;

    authData.current = {
      auth_token,
      user_id,
      qr_code_origin,
      jwe_token
    };

    if (authentication_status === TWO_FACTOR_AUTHENTICATION_STATUS) {
      setIsTwoFactorPopupVisible(true);
      setQrCodeOrigin(qr_code_origin);

      return;
    }

    if (SpringConfigs.ADD_INFO_AFTER_LOGIN) {
      dispatch(setShowAdditionalInfo(true));
      LocalStorage.setItem(
        storageKeyName('customPopup', 'SHOW_ADDITIONAL_INFO'),
        'true'
      );
    }

    checkAndStoreFcmToken();

    updateAuthData();

    sendPostMessage({ type: 'userId', value: data.user_id }, VAIX_IFRAME_ID);

    window.popupIframe && sendRestoreLoginPostMsg(data);

    if (SpringConfigs.IFRAME_SPORTSBOOK) {
      loginState('loggedIn');
    }

    if (
      SpringConfigs.LOGIN_LIMIT_POPUP ||
      SpringConfigs.PLAYER_ACTIVITY_CHECK
    ) {
      setUserLoginLimitStartTimeLS();
    }
  };

  const updateAuthData = () => {
    LocalStorage.setItem(
      storageKeyName('account', 'AUTH_DATA'),
      JSON.stringify(authData.current)
    );
    getUser(
      (data: ProfileData) => {
        storeUser(data);

        if (
          data.terms_and_conditions_version !== 'v2' &&
          SpringConfigs.SHOW_ADDITIONAL_CHECKBOX_FOR_LOGIN &&
          SpringConfigs.VERIFICATION_TYPE === 0
        ) {
          setTermsAndConditionVersion('v2', () => {});
        }
      },
      updateUser,
      'login',
      false,
      !!(
        personalId?.editForm.showOnEditProfile &&
        personalId.editForm.personalIdType === '2' &&
        personalId?.editForm.sendCPFAsDocNumber === '1'
      )
    );

    if (SpringConfigs.PUSH_NOTIFICATIONS_ENABLED) {
      getUserAllNotifications();
    }
  };

  const { updateUser } = useUpdateUser();

  const storeUser = (userData: ProfileData) => {
    setCookieLoginCredentials('loginCredentials', {
      rememberMe,
      userGroups: userGroups || '',
      jwe_token: authData?.current?.jwe_token || ''
    });

    dispatch(
      setUserData({
        ...userData,
        last_login_date:
          userData.last_login_date ||
          Math.floor(new Date().getTime() / MILLISECONDS_IN_SECOND)
      })
    );
    dispatch(
      setLoginLimit((userData.session_duration || 0) * SECONDS_PER_MINUTE)
    );

    userData.active_time_in_casino &&
      SessionStorage.setItem(
        storageKeyName('account', 'ACTIVE_TIME_IN_CASINO'),
        `${userData.active_time_in_casino}`
      );

    const afterSignIn = new AfterSignIn();
    afterSignIn.setAfterExecutionCallback(() => {
      if (afterSignIn.isActionsExist()) {
        !inPopup && getSavedLocation();
      } else {
        const savedLocation = getSavedLocation();

        const splitLocation = savedLocation.split('?');

        const parsedQuery = qs.parse(splitLocation[1], {
          ignoreQueryPrefix: true
        }) as Record<string, string>;

        if (parsedQuery['accounts'] && parsedQuery['login']) {
          delete parsedQuery.accounts;
          delete parsedQuery.login;
        }

        const updatedLocation = `${splitLocation[0]}?${qs.stringify(
          parsedQuery
        )}`;

        history.push(updatedLocation);
      }

      dispatch(setUserLoggedIn(true));
      LocalStorage.setItem(
        storageKeyName('account', 'PARENT_ACCOUNT_CURRENCY'),
        userData.currency
      );

      setCookieLoginCredentials(
        'sbuser',
        {
          rememberMe,
          userGroups: userGroups ? userGroups || '' : ''
        },
        CookieExpiration.DAYS_15
      );
    });
    afterSignIn.executeActions();
  };

  return {
    loginCallback,
    updateAuthData,
    isTwoFactorPopupVisible,
    qrCodeOrigin
  };
};
